/**
 * Audience rule for the four extra tenant languages (COL-478).
 *
 * This file used to carry a feature flag as well. `VITE_TENANT_LANGUAGES` was
 * a Vercel environment variable that shipped FR / AR / UK / ES dark: off
 * everywhere, the Settings card and the first-login picker showed German and
 * English only and a stored preference outside de/en rendered as German. The
 * flag is retired — product took the four languages live, the copy for them
 * is seeded (20261021190502), and the only thing the flag still did was make
 * the header switch look broken on production while working locally. What
 * governs the six now is the audience rule below, and nothing else.
 *
 * Audience: a signed-in account may pick any of the six ON THE SURFACES THAT
 * SPEAK THEM — the tenant dashboard and its flows, plus the two portals'
 * Settings cards (so staff can hold a preference to check the dashboard, and
 * the stored choice is always visible where it is edited). The marketing
 * pages offer German and English only: they carry inline German/English
 * dictionaries, are not translated, and a six-language menu there promised
 * languages the site cannot speak (Neel, 6 Oct — the header showed Українська
 * while the page stayed English). Signed-out visitors keep de/en everywhere.
 *
 * This audience rule has moved twice. Decision 6 on COL-477 gated the PERSON
 * (tenants only) — wrong thing, staff could not test the dashboard. #1716
 * gated nothing (any signed-in account, any surface) — too loose, the
 * marketing site advertised six. The rule that holds is the SURFACE one:
 * `isTenantSurface` names where the four new languages actually render, and
 * `rendersInBaseLanguage` keeps every other surface — the marketing site and
 * both portals under /admin — left-to-right in German or English, whatever
 * the account has chosen.
 */
import {
  BASE_LANGUAGES,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "./languages";

export type LanguageAudience = {
  /** True once an account is known. Signed-out visitors keep de/en. */
  isSignedIn: boolean;
  /**
   * True on surfaces where offering all six makes sense: the tenant surfaces
   * (which render them) and the portals under /admin (whose Settings cards
   * edit the stored preference). False on the marketing site, which speaks
   * German and English only — see `offersAllLanguages`.
   */
  surfaceOffersAll: boolean;
};

/** Languages an account may pick. */
export function selectableLanguages(
  audience: LanguageAudience
): readonly SupportedLanguage[] {
  return audience.isSignedIn && audience.surfaceOffersAll
    ? SUPPORTED_LANGUAGES
    : BASE_LANGUAGES;
}

/**
 * The routes actually translated into the four new languages (COL-479): the
 * tenant dashboard, the handover protocol flow, and the deposit-release
 * signature flow. Everything the tenant reaches signed-in, and nothing else.
 */
const TENANT_SURFACE_PREFIXES = [
  "/dashboard",
  "/protocol",
  "/deposit-release",
] as const;

export function isTenantSurface(pathname: string): boolean {
  // React Router matches routes case-insensitively, so /Dashboard renders the
  // dashboard while location.pathname keeps its original casing — classify on
  // the lowercased path or that URL would wrongly render as marketing.
  const path = pathname.toLowerCase();
  return TENANT_SURFACE_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

function isPortalSurface(pathname: string): boolean {
  const path = pathname.toLowerCase();
  return path === "/admin" || path.startsWith("/admin/");
}

/**
 * Surfaces whose language controls may OFFER all six: the tenant surfaces
 * (they render them) and the portals (their Settings cards must show and edit
 * the stored preference — an owner who picked Ukrainian has to see Ukrainian
 * selected at /admin/account, and staff need the menu to put the tenant
 * dashboard into French and check their own work, per #1716).
 *
 * The marketing site is deliberately NOT here: it renders German/English only,
 * so its header offers exactly those two (COL-477 follow-up, Neel 6 Oct).
 */
export function offersAllLanguages(pathname: string): boolean {
  return isTenantSurface(pathname) || isPortalSurface(pathname);
}

/**
 * Surfaces that render in German or English and stay left-to-right whatever
 * the account chose: everything except the tenant surfaces — the admin
 * console and owner portal under /admin, and the whole marketing site, none
 * of which are translated into the four new languages.
 *
 * Scoping this to the route rather than the account means an Arabic-reading
 * admin gets a mirrored tenant dashboard — correct, it is translated — and an
 * unmirrored console and marketing site.
 */
export function rendersInBaseLanguage(pathname: string): boolean {
  return !isTenantSurface(pathname);
}
